import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { Search } from "lucide-react";
import {
  useBackdropDismissGuard,
  useModalAccessibility,
} from "@/components/ui/overlay";
import { cn } from "@/lib/utils";
import { matchesTextQuery } from "@/lib/textSearch";

export type QuickSearchDestination = {
  id: string;
  label: string;
  path: string;
  icon: ReactNode;
  /** Parent section label shown as a muted breadcrumb. */
  group?: string;
};

export type QuickSearchDialogProps = {
  destinations: readonly QuickSearchDestination[];
  /** Controlled so the top bar search button can open it too. */
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
};

export function quickSearchShortcutLabel(): string {
  return typeof navigator !== "undefined" &&
    /Mac|iPhone|iPad|iPod/i.test(navigator.platform)
    ? "⌘K"
    : "Ctrl+K";
}

export function QuickSearchDialog({
  destinations,
  isOpen,
  onOpenChange,
}: QuickSearchDialogProps) {
  const { t } = useTranslation("common");
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(0);
  const dialogRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const isOpenRef = useRef(isOpen);
  isOpenRef.current = isOpen;
  const dialogId = useId();
  const titleId = `${dialogId}-title`;
  const listId = `${dialogId}-list`;
  const guardBackdropDismiss = useBackdropDismissGuard(isOpen);

  const close = useCallback(() => {
    onOpenChange(false);
    setQuery("");
    setSelectedIndex(0);
  }, [onOpenChange]);

  useModalAccessibility({
    isOpen,
    onClose: close,
    closeOnEscape: true,
    dialogRef,
  });

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (!(event.metaKey || event.ctrlKey) || event.key.toLowerCase() !== "k") {
        return;
      }
      // Allow Cmd/Ctrl+K even from inputs — same as Linear/Vercel.
      event.preventDefault();
      if (isOpenRef.current) {
        close();
        return;
      }
      onOpenChange(true);
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [close, onOpenChange]);

  const results = useMemo(() => {
    return destinations.filter((destination) =>
      matchesTextQuery(query, [
        destination.label,
        destination.group,
        destination.path,
      ]),
    );
  }, [destinations, query]);

  useEffect(() => {
    setSelectedIndex(0);
  }, [query]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    const frame = window.requestAnimationFrame(() => {
      inputRef.current?.focus();
      inputRef.current?.select();
    });
    return () => window.cancelAnimationFrame(frame);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    const option = listRef.current?.querySelector<HTMLElement>(
      `[data-quick-search-index="${selectedIndex}"]`,
    );
    option?.scrollIntoView({ block: "nearest" });
  }, [isOpen, selectedIndex, results.length]);

  const goTo = useCallback(
    (destination: QuickSearchDestination) => {
      close();
      void navigate(destination.path);
    },
    [close, navigate],
  );

  const onInputKeyDown = (event: ReactKeyboardEvent<HTMLInputElement>) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      if (results.length === 0) return;
      setSelectedIndex((index) => (index + 1) % results.length);
      return;
    }
    if (event.key === "ArrowUp") {
      event.preventDefault();
      if (results.length === 0) return;
      setSelectedIndex((index) =>
        index <= 0 ? results.length - 1 : index - 1,
      );
      return;
    }
    if (event.key === "Enter") {
      event.preventDefault();
      const destination = results[selectedIndex];
      if (destination) {
        goTo(destination);
      }
    }
  };

  if (!isOpen) {
    return null;
  }

  const shortcutLabel = quickSearchShortcutLabel();

  return createPortal(
    <div
      className={cn(
        "fixed inset-0 z-[60] flex items-start justify-center",
        "animate-fade-in bg-surface-950/40 px-3 pb-3 pt-[min(18vh,8rem)] backdrop-blur-[2px] sm:px-4 dark:bg-black/70",
      )}
      onClick={() => guardBackdropDismiss(close)}
    >
      <div
        ref={dialogRef}
        id={dialogId}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className={cn(
          "relative w-full max-w-xl animate-scale-in overflow-hidden rounded-xl border border-surface-200 bg-surface-0 shadow-popover outline-none",
          "flex max-h-[min(28rem,calc(100dvh-6rem))] flex-col",
        )}
        onClick={(event) => event.stopPropagation()}
      >
        <h2 id={titleId} className="sr-only">
          {t("quickSearch.title")}
        </h2>
        <div className="flex shrink-0 items-center gap-3 border-b border-surface-200/70 px-4 py-3.5">
          <Search className="h-[18px] w-[18px] shrink-0 text-surface-400" aria-hidden />
          <input
            ref={inputRef}
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={onInputKeyDown}
            placeholder={t("quickSearch.placeholder")}
            className={cn(
              "min-w-0 flex-1 bg-transparent text-base text-surface-900 sm:text-[15px] outline-none focus-visible:ring-0 focus-visible:ring-offset-0 [&::-webkit-search-cancel-button]:hidden",
              "placeholder:text-surface-400",
            )}
            aria-controls={listId}
            aria-autocomplete="list"
            aria-activedescendant={
              results[selectedIndex]
                ? `${dialogId}-option-${results[selectedIndex].id}`
                : undefined
            }
            autoComplete="off"
            autoCorrect="off"
            spellCheck={false}
          />
          <kbd className="hidden shrink-0 rounded-md border border-surface-200 bg-surface-50 px-1.5 py-0.5 font-mono text-[11px] font-medium text-surface-500 sm:inline">
            {shortcutLabel}
          </kbd>
        </div>

        <ul
          ref={listRef}
          id={listId}
          role="listbox"
          aria-label={t("quickSearch.title")}
          className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-1.5"
        >
          {results.length === 0 ? (
            <li className="px-3 py-8 text-center text-sm text-surface-500">
              {t("quickSearch.empty")}
            </li>
          ) : (
            results.map((destination, index) => {
              const selected = index === selectedIndex;
              return (
                <li
                  key={destination.id}
                  id={`${dialogId}-option-${destination.id}`}
                  role="option"
                  aria-selected={selected}
                  data-quick-search-index={index}
                >
                  <button
                    type="button"
                    className={cn(
                      "flex w-full items-center gap-3 rounded-lg px-2.5 py-2 text-left text-sm transition-colors",
                      selected
                        ? "bg-surface-100 text-surface-900"
                        : "text-surface-700",
                    )}
                    onMouseEnter={() => setSelectedIndex(index)}
                    onClick={() => goTo(destination)}
                  >
                    <span
                      className={cn(
                        "flex h-7 w-7 shrink-0 items-center justify-center rounded-md border border-surface-200 bg-surface-0",
                        selected ? "text-primary-600" : "text-surface-500",
                      )}
                    >
                      {destination.icon}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate font-medium">
                        {destination.label}
                      </span>
                      {destination.group ? (
                        <span className="block truncate text-xs text-surface-500">
                          {destination.group}
                        </span>
                      ) : null}
                    </span>
                  </button>
                </li>
              );
            })
          )}
        </ul>

        <div className="shrink-0 border-t border-surface-200/70 bg-surface-50/70 px-4 py-2 text-[11px] text-surface-500">
          {t("quickSearch.hint")}
        </div>
      </div>
    </div>,
    document.body,
  );
}
